import { ReportPdfData } from '../types';
import { getFormattedDate } from '../utils';
import { PdfAssets } from '../../pdfTemplates';

/**
 * Creates the cover page for the PDF report
 * @param data Report data
 * @param assets Logo and font assets for the PDF
 * @param colors Color palette for styling
 * @returns Cover page content for pdfmake
 */
export const createCoverPage = (
  data: ReportPdfData,
  assets: PdfAssets | undefined,
  colors: Record<string, string>
) => {
  // Format the date for display
  const formattedDate = getFormattedDate();

  return {
    stack: [
      // Eyebrow
      {
        canvas: [
          {
            type: 'rect',
            x: 0,
            y: 0,
            w: 595, // Page width minus left padding (64px) * 2
            h: 51,
            color: colors.bgGrey
          }
        ],
        margin: [64, 0, 0, 0]
      },
      
      // Logo Section
      assets?.logoUrl ? {
        image: assets.logoUrl,
        width: 140,
        absolutePosition: { x: 64, y: 120 }
      } : { 
        text: 'MetaRouter', 
        fontSize: 24, 
        bold: true, 
        absolutePosition: { x: 64, y: 120 }
      },
      
      // Title Section
      { 
        text: data.title, 
        fontSize: 32,
        lineHeight: 0.8,
        bold: true, 
        color: colors.black, 
        margin: [64, 256, 64, 0]
      },
      
      // Subtitle with bar
      {
        columns: [
          {
            text: 'MetaRouter Infrastructure Report',
            fontSize: 11,
            color: colors.black,
            width: 'auto'
          },
          {
            canvas: [
              {
                type: 'rect',
                x: 8,
                y: 5,
                w: 64,
                h: 2,
                color: colors.primary
              }
            ],
            width: 'auto'
          }
        ],
        columnGap: 16,
        margin: [64, 32, 0, 0]
      },
      
      // Footer
      {
        canvas: [
          {
            type: 'rect',
            x: 0,
            y: 0,
            w: 595 - 64, // Page width minus left padding
            h: 264,
            color: colors.bgGrey
          }
        ],
        absolutePosition: { x: 64, y: 586 } // Fixed position at bottom right
      },
      
      // Prepared by section
      {
        stack: [
          {
            text: 'Prepared by',
            fontSize: 10,
            color: colors.black,
            margin: [0, 0, 0, 6]
          },
          {
            text: data.authorName || data.authorEmail,
            fontSize: 12,
            bold: true,
            color: colors.black,
            lineHeight: 1.2,
          },
          {
            text: data.ownerRole || '',
            fontSize: 10,
            color: colors.black,
            lineHeight: 1.2,
          },
          ...(typeof data.authorName === 'string' && data.authorName.trim() !== '' ? [{
            text: data.authorEmail,
            fontSize: 10,
            color: colors.black,
          }] : [])
        ],
        absolutePosition: { x: 104, y: 625 }
      },
      
      // Bottom footer section
      {
        columns: [
          {
            stack: [
              {
                columns: [
                  {
                    text: 'Generated on:',
                    fontSize: 10,
                    color: colors.black,
                    width: 'auto'
                  },
                  {
                    text: formattedDate,
                    fontSize: 10,
                    color: colors.black,
                    width: 'auto',
                    margin: [4, 0, 0, 0]
                  }
                ],
                columnGap: 0,
                absolutePosition: { x: 104, y: 770 }
              }
            ],
            width: '*'
          },
          {
            image: assets?.architectUrl ? assets.architectUrl : '',
            width: 52,
            height: 10,
            absolutePosition: { x: 595 - 64 - 52, y: 770 }
          }
        ],
        margin: [4, 0, 0, 0]
      }
    ],
    pageBreak: 'after'
  };
};
